import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { temporal } from 'zundo'
import {
  DEFAULT_MATCH_TIERS,
  PLAN_LIMITS,
} from '../lib/constants.js'
import { calculatePlanResults } from '../lib/calculations.js'
import { calculateTurnover } from '../lib/turnover.js'
import {
  copyPlanWithName,
  createDefaultPlan,
  mergePlan,
  normalizePlan,
  normalizePlanSettings,
  pickPlanSettings,
  PLAN_SETTINGS_VERSION,
  normalizeEmployerProfile,
} from '../lib/planSettings.js'
import {
  CENSUS_MODES,
  createExampleEmployees,
  normalizePersistedCensus,
} from '../lib/demoEntitlements.js'
import { generateSyntheticCensus, normalizeEstimatorInputs } from '../lib/estimator.js'
import { DEFAULT_REPORT_CONFIG, resolveReportConfig } from '../lib/reportConfig.js'
import {
  createEmptySlotLocal,
  legacyGroupLabelsById,
  normalizeCensusLocal,
  pruneEmployeeFromSlotLocal,
} from '../lib/planDesign.js'
import { buildNewCompScenarioApplication } from '../lib/newCompScenarioApply.js'
import { projectClassAssignment, projectIndividualClasses } from '../lib/allocationClasses.js'

const deepClone = (value) => JSON.parse(JSON.stringify(value))
const createDefaultEmployees = () => createExampleEmployees()

// ---------------------------------------------------------------------------
// censusLocal sidecar (§2.2): browser-only, slot-scoped participant-linked
// design state (solver targets, per-person amounts). Class membership is NOT
// here — it is the census row's own allocation_group code. This sidecar has
// the same sensitivity as the census itself: it is excluded from
// getPlanSettings(), autosave, Client Book payloads, and portable plan files,
// and must never reach analytics, telemetry, logging, or LLM payloads.
// ---------------------------------------------------------------------------

// Opaque generated ID, never derived from row contents (§2.2).
const mintCensusInstanceId = () => (
  globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `census-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
)

const emptyCensusLocal = () => ({ censusInstanceId: null, byPlanKey: {} })

const freshCensusLocal = (employees) => ({
  censusInstanceId: Array.isArray(employees) && employees.length ? mintCensusInstanceId() : null,
  byPlanKey: {},
})

// Estimate-mode rows originally used an EST prefix. Keep any user-authored IDs
// intact, but migrate those generated IDs to the same EMP convention used by
// the built-in sample census. Reports and exports read these rows directly, so
// this keeps every surface aligned without presentation-only substitutions.
const migrateEstimateEmployeeIds = (employees) => {
  const idMap = {}
  const rows = Array.isArray(employees) ? employees : []
  const migrated = rows.map((employee) => {
    const currentId = String(employee?.id || '').trim()
    const match = /^EST(\d+)$/i.exec(currentId)
    if (!match) return employee
    const nextId = `EMP${match[1].padStart(3, '0')}`
    idMap[currentId] = nextId
    return { ...employee, id: nextId }
  })
  return { employees: migrated, idMap }
}

const remapCensusLocalEmployeeIds = (value, idMap) => {
  const local = normalizeCensusLocal(value)
  if (Object.keys(idMap).length === 0) return local
  return {
    ...local,
    byPlanKey: Object.fromEntries(Object.entries(local.byPlanKey).map(([planKey, slot]) => [
      planKey,
      {
        solverTargets: {
          profitSharing: slot.solverTargets.profitSharing.map((id) => idMap[id] || id),
        },
        perPersonAmounts: {
          profitSharing: Object.fromEntries(
            Object.entries(slot.perPersonAmounts.profitSharing)
              .map(([id, amount]) => [idMap[id] || id, amount]),
          ),
        },
      },
    ])),
  }
}

const pruneEmployeeEverywhere = (censusLocal, employeeId) => ({
  censusInstanceId: censusLocal.censusInstanceId,
  byPlanKey: Object.fromEntries(
    Object.entries(censusLocal.byPlanKey).map(([key, slotLocal]) => [
      key,
      pruneEmployeeFromSlotLocal(slotLocal, employeeId),
    ]),
  ),
})

// Plan design slots. Plan A always exists and is the baseline every other plan
// is compared against. Plan B–D are alternative designs: a slot holding a plan
// means "this design is in the comparison"; a null slot is empty. There is no
// separate compare flag – comparing IS having more than one plan.
export const PLAN_SLOTS = ['planA', 'planB', 'planC', 'planD']
export const PLAN_SLOT_NAMES = { planA: 'Plan A', planB: 'Plan B', planC: 'Plan C', planD: 'Plan D' }

// Stage shell (Phase 4): the numbered ①People ②Plan ③Results ④Reports
// progression. Allocation classes are a census column, so they live on People
// rather than in a stage of their own. The URL hash is canonical when present; this persisted value is
// only the initial-load fallback (§7 rule 1).
export const WORKSPACE_STAGES = ['people', 'plan', 'checks', 'results', 'reports']

export const presentPlanKeys = (state) => PLAN_SLOTS.filter((key) => state[key])
export const visiblePlanKeys = (state) => {
  const hidden = new Set(Array.isArray(state.hiddenPlanKeys) ? state.hiddenPlanKeys : [])
  return presentPlanKeys(state).filter((key) => key === 'planA' || !hidden.has(key))
}
export const isComparing = (state) => visiblePlanKeys(state).length > 1
// Legacy single-plan consumers resolve to Plan A whenever multiple designs are
// visible. `activePlanKey` remains in persisted/imported state for backward
// compatibility and explicit assistant targets, but it is not comparison UI
// state: visible plans participate together and Plan A is always the baseline.
export const resolveActivePlanKey = (state) => {
  const visible = visiblePlanKeys(state)
  if (visible.length > 1) return 'planA'
  return visible.includes(state.activePlanKey) ? state.activePlanKey : (visible[0] || 'planA')
}

const normalizeHiddenPlanKeys = (value, plans = {}) => {
  const incoming = Array.isArray(value) ? value : []
  return PLAN_SLOTS.filter((key) => key !== 'planA' && plans[key] && incoming.includes(key))
}

const normalizePlanTemplates = (value) => {
  if (!Array.isArray(value)) return []
  const seen = new Set()
  return value.flatMap((entry, index) => {
    if (!entry || typeof entry !== 'object' || !entry.plan) return []
    const name = String(entry.name || '').trim().replace(/\s+/g, ' ').slice(0, 40)
    const id = String(entry.id || `saved-design-${index + 1}`).trim().slice(0, 80)
    if (!name || !id || seen.has(id)) return []
    seen.add(id)
    return [{
      id,
      name,
      plan: normalizePlan(entry.plan, name),
      savedAt: String(entry.savedAt || ''),
    }]
  }).slice(0, 20)
}

// Plan A defaults to the "Safe Harbor + MBDR" preset (see PLAN_PRESETS in
// CalculatorWorkspace): enhanced match, immediate vesting, after-tax Roth path.
const createPlanA = () => normalizePlan({
  ...createDefaultPlan('Plan A'),
  employerContributions: {
    ...createDefaultPlan('Plan A').employerContributions,
    matchEnabled: true,
    matchTiers: [
      { matchPercent: 100, upToPercent: 4 },
      { matchPercent: 50, upToPercent: 6 },
    ],
  },
  vesting: { ...createDefaultPlan('Plan A').vesting, enabled: true },
  afterTax: { allowed: true, maxPercent: 0, maxDollar: 15000 },
}, 'Plan A')

// Journey rail progress (Add people → Set plan → Review results → Export).
// Persisted but never undo-tracked: progress belongs to the user, not the
// census, so it is also left out of the estimate-mode stash/restore.
const JOURNEY_KEYS = ['planTouched', 'resultsViewed', 'exported', 'railCollapsed']
const createDefaultJourney = () => ({
  planTouched: false,
  resultsViewed: false,
  exported: false,
  railCollapsed: false,
})
export const normalizeJourney = (value) => {
  const journey = createDefaultJourney()
  if (!value || typeof value !== 'object') return journey
  for (const key of JOURNEY_KEYS) journey[key] = value[key] === true
  return journey
}

const emptyAnalysis = () => ({
  calculatedEmployees: [],
  metrics: null,
  chartData: null,
  turnover: null,
})

const calculateAnalysis = (employees, plan, planKey = null, censusLocal = null) => {
  const planResults = calculatePlanResults(employees, plan, { planKey, censusLocal })
  const calculatedEmployees = planResults.calculatedEmployees
  const shortTermDays = plan.terminationHandling?.shortTermThresholdDays ?? 90

  return {
    calculatedEmployees,
    metrics: planResults.metrics,
    chartData: planResults.chartData,
    allocationDiagnostics: planResults.allocationDiagnostics,
    turnover: employees.length > 0
      ? calculateTurnover(employees, plan.planYear, shortTermDays)
      : null,
  }
}

// Authored inputs and every result derived from them are published in the same
// Zustand transaction. Components never observe a new census/plan alongside
// analysis from the previous snapshot. Pass planKeys only when the mutation is
// provably isolated to those plans; census-wide changes recalculate all slots.
const withRecalculatedAnalysis = (state, patchOrFactory, planKeys = null) => {
  const patch = typeof patchOrFactory === 'function' ? patchOrFactory(state) : patchOrFactory
  if (!patch || patch === state) return state
  const next = { ...state, ...patch }
  const keys = Array.isArray(planKeys) ? planKeys : PLAN_SLOTS
  const analysis = Array.isArray(planKeys) ? { ...state.analysis } : {}
  for (const key of keys) {
    analysis[key] = next[key]
      ? calculateAnalysis(next.employees, next[key], key, next.censusLocal)
      : emptyAnalysis()
  }
  return {
    ...patch,
    analysis,
    lastCalculation: new Date().toISOString(),
  }
}

const planFromPresetSettings = (currentPlan, rawSettings) => {
  if (!currentPlan || !rawSettings || typeof rawSettings !== 'object') return null
  const requiredSlices = ['employerContributions', 'vesting', 'afterTax', 'cashBalance']
  if (!requiredSlices.every((key) => rawSettings[key] && typeof rawSettings[key] === 'object')) return null

  // A preset replaces every field in the slices it owns. Unrelated document
  // settings (eligibility, termination handling, testing elections) remain;
  // proposal provenance is discarded because it describes the design being
  // replaced and must never survive into a different preset.
  const { newCompProposal: _discardedProposal, ...preservedPlan } = currentPlan
  return normalizePlan({
    ...preservedPlan,
    ...(Object.prototype.hasOwnProperty.call(rawSettings, 'planYear')
      ? { planYear: rawSettings.planYear }
      : {}),
    employerContributions: deepClone(rawSettings.employerContributions),
    vesting: deepClone(rawSettings.vesting),
    afterTax: deepClone(rawSettings.afterTax),
    cashBalance: deepClone(rawSettings.cashBalance),
  }, currentPlan.name)
}

const getHistoryState = state => ({
  employees: state.employees,
  censusMode: state.censusMode,
  censusBaseline: state.censusBaseline,
  planA: state.planA,
  planB: state.planB,
  planC: state.planC,
  planD: state.planD,
  // A plan edit and its related participant-linked local edit are one undo
  // transaction (§2.2).
  censusLocal: state.censusLocal,
  inputErrors: state.inputErrors,
  employerProfile: state.employerProfile,
  settingMetadataByPath: state.settingMetadataByPath,
  planTemplates: state.planTemplates,
})

const HISTORY_KEYS = ['employees', 'censusMode', 'censusBaseline', ...PLAN_SLOTS, 'censusLocal', 'inputErrors', 'employerProfile', 'settingMetadataByPath', 'planTemplates']
const historyStatesEqual = (pastState, currentState) => (
  HISTORY_KEYS.every(key => Object.is(pastState[key], currentState[key]))
)

const normalizeStoredPlans = (source = {}) => Object.fromEntries(PLAN_SLOTS.map((key) => [
  key,
  key === 'planA'
    ? normalizePlan(source.planA, PLAN_SLOT_NAMES[key])
    : source[key] && typeof source[key] === 'object'
      ? normalizePlan(source[key], PLAN_SLOT_NAMES[key])
      : null,
]))

// v20 fold: membership used to live in censusLocal.groupAssignments keyed by a
// plan-authored group id. It now lives on the census row's allocation_group.
// Recover the id → label map from the ACTIVE plan's retired allocationGroups
// array and stamp the label onto each assigned row, so an upgrading workspace
// keeps the classes its rates already point at. Rows that already carry a code
// are left alone; per-source overrides collapse to the default assignment,
// which is the only one the census column can express.
const foldGroupAssignmentsOntoCensus = (employees, source) => {
  const rows = Array.isArray(employees) ? employees : []
  const activeKey = ['planA', 'planB', 'planC', 'planD'].includes(source.activePlanKey)
    ? source.activePlanKey : 'planA'
  const labelsById = legacyGroupLabelsById(source[activeKey]?.allocationGroups)
  const assignments = source.censusLocal?.byPlanKey?.[activeKey]?.groupAssignments
  if (!assignments || !rows.length) return rows
  const bySource = assignments.bySource || {}
  const assignedId = (employeeId) => (
    bySource.profitSharing?.[employeeId]
    ?? assignments.default?.[employeeId]
    ?? bySource.match?.[employeeId]
    ?? bySource.cashBalance?.[employeeId]
    ?? null
  )
  return rows.map((employee) => {
    if (String(employee?.allocation_group || '').trim()) return employee
    const label = labelsById[assignedId(employee?.id)]
    return label ? { ...employee, allocation_group: label } : employee
  })
}

const normalizeStoredBundle = (source = {}) => {
  const census = normalizePersistedCensus(
    foldGroupAssignmentsOntoCensus(source.employees, source),
    source.censusMode,
    foldGroupAssignmentsOntoCensus(source.censusBaseline, source),
  )
  const plans = normalizeStoredPlans(source)
  const local = normalizeCensusLocal(source.censusLocal)
  // A workspace holding a census always carries an instance ID, so anything
  // later written to the sidecar is tied to a known census generation.
  const censusLocal = local.censusInstanceId || census.employees.length === 0
    ? local
    : { ...local, censusInstanceId: mintCensusInstanceId() }
  return { ...census, ...plans, censusLocal }
}

// Persisted browser state has one supported shape. On a storage-version change,
// retain current fields and normalize them through the same authored contract
// used by imports and cloud records; unsupported fields are dropped.
export const migratePersistedState = (persistedState) => {
  const source = persistedState && typeof persistedState === 'object' ? persistedState : {}
  const normalizedBundle = normalizeStoredBundle(source)
  const inEstimate = source.workspaceMode === 'estimate' && source.estimator?.inputs
  const estimateIds = inEstimate
    ? migrateEstimateEmployeeIds(normalizedBundle.employees)
    : { employees: normalizedBundle.employees, idMap: {} }
  const bundle = inEstimate
    ? {
        ...normalizedBundle,
        employees: estimateIds.employees,
        censusLocal: remapCensusLocalEmployeeIds(normalizedBundle.censusLocal, estimateIds.idMap),
      }
    : normalizedBundle
  const stashedBundle = inEstimate && source.stashedActual
    ? normalizeStoredBundle(source.stashedActual)
    : null
  const stashedActual = stashedBundle
    ? {
        ...stashedBundle,
        employerProfile: normalizeEmployerProfile(source.stashedActual.employerProfile),
        settingMetadataByPath: source.stashedActual.settingMetadataByPath || {},
        activePlanKey: source.stashedActual[source.stashedActual.activePlanKey]
          ? source.stashedActual.activePlanKey : 'planA',
        hiddenPlanKeys: normalizeHiddenPlanKeys(source.stashedActual.hiddenPlanKeys, stashedBundle),
      }
    : null

  return {
    ...bundle,
    hiddenPlanKeys: normalizeHiddenPlanKeys(source.hiddenPlanKeys, bundle),
    planTemplates: normalizePlanTemplates(source.planTemplates),
    employerProfile: normalizeEmployerProfile(source.employerProfile),
    settingMetadataByPath: source.settingMetadataByPath || {},
    customCharts: Array.isArray(source.customCharts) ? source.customCharts : [],
    reportConfig: source.reportConfig || deepClone(DEFAULT_REPORT_CONFIG),
    workspaceMode: inEstimate ? 'estimate' : 'actual',
    estimator: inEstimate ? { inputs: normalizeEstimatorInputs(source.estimator.inputs) } : null,
    stashedActual,
    journey: normalizeJourney(source.journey),
    workspaceStage: WORKSPACE_STAGES.includes(source.workspaceStage) ? source.workspaceStage : 'people',
  }
}

const useStore = create(
  persist(
    temporal(
      (set, get) => ({
        employees: createDefaultEmployees(),
        censusMode: CENSUS_MODES.EXAMPLE,
        censusBaseline: createDefaultEmployees(),
        censusLocal: freshCensusLocal(createDefaultEmployees()),
        planA: createPlanA(),
        planB: null,
        planC: null,
        planD: null,
        // Which plan the single-plan surfaces (plan editor, employee results,
        // charts) show.
        activePlanKey: 'planA',
        // One shared visibility list drives Plan Settings, Checks, Results,
        // charts, and Reports. Hidden designs stay configured; Plan A remains
        // visible because it is the comparison baseline.
        hiddenPlanKeys: [],
        // Personal, census-free shortcuts for designs the user applies often.
        // These remain in this browser and are intentionally excluded from
        // plan files, Client Book payloads, and analytics. Template changes are
        // undoable like the other actions in the Plans ledger.
        planTemplates: [],
        // Estimate Mode (prospecting): 'estimate' swaps in a synthetic census
        // generated from rough assumptions; the real workspace is stashed and
        // restored on exit. Undo history is cleared at both boundaries so
        // estimate and actual data can never cross-contaminate via undo.
        workspaceMode: 'actual',
        estimator: null, // { inputs } while in estimate mode
        stashedActual: null, // { employees, censusMode, censusBaseline, planA..planD, activePlanKey }
        analysis: Object.fromEntries(PLAN_SLOTS.map((key) => [key, emptyAnalysis()])),
        lastCalculation: null,
        inputErrors: {},
        inputValidationResetKey: 0,
        employerProfile: normalizeEmployerProfile(),
        settingMetadataByPath: {},

        utilityDialog: null,
        visualizationTab: 'overview',
        isDataExpanded: true,
        isCalculationsExpanded: true,

        // Charts the user saved from Julio. Persisted to localStorage (survives
        // closing the assistant) but kept out of the undo history and the metric
        // recompute – they are just stored configs the visualization tab renders.
        customCharts: [],

        // PDF report customizer settings (title, firm/client, prepared-by, custom
        // logo, debrand, which sections to include). Persisted; not undo-tracked.
        reportConfig: deepClone(DEFAULT_REPORT_CONFIG),

        // Stage shell position. Never undo-tracked (navigation belongs to the
        // user, not the census) and never part of plan settings.
        workspaceStage: 'people',
        // The ONE hashchange/navigation entry point (§7 rule 1): every stage
        // change flows through here; the shell mirrors state to the URL hash
        // with replaceState, which never re-fires hashchange, so no sync loop
        // can form.
        setWorkspaceStage: (stage) => {
          if (!WORKSPACE_STAGES.includes(stage)) return
          if (get().workspaceStage === stage) return
          const history = useStore.temporal.getState()
          const wasTracking = history.isTracking
          if (wasTracking) history.pause()
          try {
            set({ workspaceStage: stage })
          } finally {
            if (wasTracking) history.resume()
          }
        },

        journey: createDefaultJourney(),
        setJourneyFlag: (key, value = true) => {
          if (!JOURNEY_KEYS.includes(key)) return
          const flag = Boolean(value)
          if (get().journey[key] === flag) return
          const history = useStore.temporal.getState()
          const wasTracking = history.isTracking
          if (wasTracking) history.pause()
          try {
            set((state) => ({ journey: { ...state.journey, [key]: flag } }))
          } finally {
            if (wasTracking) history.resume()
          }
        },

        setInputError: (id, error, { track = true } = {}) => {
          if (!id) return
          const updateError = () => set((state) => {
            const message = typeof error === 'string' ? error : error?.message
            const hasExisting = Object.prototype.hasOwnProperty.call(state.inputErrors, id)
            if (!message && !hasExisting) return state

            const nextError = message
              ? {
                  message,
                  label: typeof error === 'string' ? id : (error.label || id),
                  target: typeof error === 'string' ? { validationId: id } : (error.target || { validationId: id }),
                  draft: typeof error === 'string' ? undefined : error.draft,
                }
              : null
            const currentError = state.inputErrors[id]
            if (
              nextError &&
              currentError?.message === nextError.message &&
              currentError?.label === nextError.label &&
              currentError?.draft === nextError.draft &&
              JSON.stringify(currentError?.target) === JSON.stringify(nextError.target)
            ) {
              return state
            }

            const inputErrors = { ...state.inputErrors }
            if (nextError) inputErrors[id] = nextError
            else delete inputErrors[id]
            return { inputErrors }
          })

          if (track) {
            updateError()
            return
          }

          const history = useStore.temporal.getState()
          const wasTracking = history.isTracking
          if (wasTracking) history.pause()
          try {
            updateError()
          } finally {
            if (wasTracking) history.resume()
          }
        },
        clearInputErrors: () => set({ inputErrors: {} }),
        resetInputValidation: () => {
          const history = useStore.temporal.getState()
          const wasTracking = history.isTracking
          if (wasTracking) history.pause()
          try {
            set((state) => withRecalculatedAnalysis(state, {
              inputErrors: {},
              inputValidationResetKey: state.inputValidationResetKey + 1,
            }))
          } finally {
            if (wasTracking) history.resume()
          }
        },

        setEmployees: (employees, { censusMode, censusBaseline } = {}) => {
          set((state) => withRecalculatedAnalysis(state, () => {
            // Passing censusMode means a census replacement (import, example,
            // synthetic): participant-linked local data belongs to the OLD
            // census, so the sidecar clears and a new instance ID is minted
            // (§2.2). Plain row-set updates keep the ID and prune entries for
            // employees that no longer exist.
            let censusLocal
            if (censusMode) {
              // Imported allocation_group codes need no seeding step: the
              // column IS the membership, and every plan slot reads the same
              // census, so an import applies to all of them at once.
              censusLocal = freshCensusLocal(employees)
            } else {
              const nextIds = new Set((employees || []).map((employee) => employee?.id))
              const removed = (state.employees || []).filter((employee) => !nextIds.has(employee?.id))
              censusLocal = removed.reduce(
                (accumulator, employee) => pruneEmployeeEverywhere(accumulator, employee.id),
                state.censusLocal,
              )
            }
            return {
              employees,
              censusMode: censusMode || state.censusMode,
              censusBaseline: censusBaseline !== undefined ? censusBaseline : state.censusBaseline,
              censusLocal,
              inputErrors: {},
              inputValidationResetKey: state.inputValidationResetKey + 1,
            }
          }))
        },
        addEmployee: (employee) => {
          set((state) => withRecalculatedAnalysis(state, {
            employees: [...state.employees, employee],
          }))
        },
        updateEmployee: (id, updates) => {
          set((state) => withRecalculatedAnalysis(state, {
            employees: state.employees.map(employee => (
              employee.id === id ? { ...employee, ...updates } : employee
            )),
            // Changing an employee's ID is delete-plus-add: the old ID's
            // assignments, targets, and amounts are pruned, never re-inferred.
            censusLocal: updates?.id !== undefined && updates.id !== id
              ? pruneEmployeeEverywhere(state.censusLocal, id)
              : state.censusLocal,
          }))
        },
        deleteEmployee: (id) => {
          set((state) => withRecalculatedAnalysis(state, {
            employees: state.employees.filter(employee => employee.id !== id),
            censusLocal: pruneEmployeeEverywhere(state.censusLocal, id),
            inputErrors: {},
            inputValidationResetKey: state.inputValidationResetKey + 1,
          }))
        },
        deleteAllEmployees: () => {
          set((state) => withRecalculatedAnalysis(state, {
            employees: [],
            censusMode: CENSUS_MODES.CUSTOM,
            censusBaseline: [],
            censusLocal: emptyCensusLocal(),
            inputErrors: {},
            inputValidationResetKey: state.inputValidationResetKey + 1,
          }))
        },
        loadExampleData: () => {
          set((state) => withRecalculatedAnalysis(state, {
            employees: createDefaultEmployees(),
            censusMode: CENSUS_MODES.EXAMPLE,
            censusBaseline: createDefaultEmployees(),
            censusLocal: freshCensusLocal(createDefaultEmployees()),
            // Loading the built-in example never overwrites a configured plan.
            planA: state.planA,
            inputErrors: {},
            inputValidationResetKey: state.inputValidationResetKey + 1,
          }))
        },

        enterEstimateMode: (rawInputs) => {
          const inputs = normalizeEstimatorInputs(rawInputs)
          const history = useStore.temporal.getState()
          history.pause()
          try {
            const syntheticEmployees = generateSyntheticCensus(inputs)
            set((state) => withRecalculatedAnalysis(state, {
              // The complete real bundle — census, plans, census instance ID,
              // and all sidecar entries — stashes together; re-entering
              // estimate mode never overwrites the original stash (§2.2).
              stashedActual: state.workspaceMode === 'estimate' ? state.stashedActual : {
                employees: state.employees,
                censusMode: state.censusMode,
                censusBaseline: state.censusBaseline,
                planA: state.planA,
                planB: state.planB,
                planC: state.planC,
                planD: state.planD,
                censusLocal: state.censusLocal,
                employerProfile: state.employerProfile,
                settingMetadataByPath: state.settingMetadataByPath,
                activePlanKey: state.activePlanKey,
                hiddenPlanKeys: state.hiddenPlanKeys,
              },
              workspaceMode: 'estimate',
              estimator: { inputs },
              employees: syntheticEmployees,
              censusMode: CENSUS_MODES.CUSTOM,
              censusBaseline: [],
              // The synthetic census gets its own ID and empty local maps, so
              // only rule-based membership resolves (§2.2).
              censusLocal: freshCensusLocal(syntheticEmployees),
              inputErrors: {},
              inputValidationResetKey: state.inputValidationResetKey + 1,
            }))
          } finally {
            history.resume()
          }
          useStore.temporal.getState().clear()
        },
        updateEstimateInputs: (rawInputs) => {
          if (get().workspaceMode !== 'estimate') return
          get().enterEstimateMode(rawInputs)
        },
        exitEstimateMode: () => {
          const state = get()
          if (state.workspaceMode !== 'estimate') return
          const stash = state.stashedActual
          const census = normalizePersistedCensus(
            stash?.employees ?? [],
            stash?.censusMode,
            stash?.censusBaseline,
          )
          const history = useStore.temporal.getState()
          history.pause()
          try {
            set((current) => withRecalculatedAnalysis(current, () => {
              const plans = {
                planA: normalizePlan(stash?.planA || current.planA, 'Plan A'),
                planB: stash?.planB ? normalizePlan(stash.planB, 'Plan B') : null,
                planC: stash?.planC ? normalizePlan(stash.planC, 'Plan C') : null,
                planD: stash?.planD ? normalizePlan(stash.planD, 'Plan D') : null,
              }
              return {
                workspaceMode: 'actual',
                estimator: null,
                stashedActual: null,
                ...census,
                ...plans,
                censusLocal: normalizeCensusLocal(stash?.censusLocal),
                employerProfile: normalizeEmployerProfile(stash?.employerProfile),
                settingMetadataByPath: stash?.settingMetadataByPath || {},
                activePlanKey: stash?.activePlanKey && plans[stash.activePlanKey] ? stash.activePlanKey : 'planA',
                hiddenPlanKeys: normalizeHiddenPlanKeys(stash?.hiddenPlanKeys, plans),
                inputErrors: {},
                inputValidationResetKey: current.inputValidationResetKey + 1,
              }
            }))
          } finally {
            history.resume()
          }
          useStore.temporal.getState().clear()
        },

        updatePlan: (planKey, updates) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey]) return
          set((state) => withRecalculatedAnalysis(state, {
            [planKey]: mergePlan(state[planKey], updates),
            journey: state.journey.planTouched ? state.journey : { ...state.journey, planTouched: true },
          }, [planKey]))
        },
        applyPlanPreset: (planKey, presetSettings) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey]) return false
          let applied = false
          set((state) => {
            const nextPlan = planFromPresetSettings(state[planKey], presetSettings)
            if (!nextPlan) return state
            applied = true
            const byPlanKey = {
              ...state.censusLocal.byPlanKey,
              [planKey]: createEmptySlotLocal(),
            }
            return withRecalculatedAnalysis(state, {
              [planKey]: nextPlan,
              censusLocal: { ...state.censusLocal, byPlanKey },
              settingMetadataByPath: { ...state.settingMetadataByPath, [planKey]: {} },
              journey: state.journey.planTouched ? state.journey : { ...state.journey, planTouched: true },
              inputErrors: {},
              inputValidationResetKey: state.inputValidationResetKey + 1,
            }, [planKey])
          })
          return applied
        },
        renamePlan: (planKey, name) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey]) return
          const nextName = String(name || '').trim().replace(/\s+/g, ' ').slice(0, 40)
          if (!nextName || get()[planKey].name === nextName) return
          set((state) => ({
            [planKey]: { ...state[planKey], name: nextName },
          }))
        },
        // --- censusLocal sidecar writers (§2.2) -----------------------------
        // Per-person amounts, solver targets, and group assignments live in
        // the browser-only sidecar, keyed by plan slot. All of these are
        // undo-tracked together with plan edits (censusLocal is a history key)
        // and never leave the device.
        setPerPersonAmount: (planKey, source, employeeId, amount) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey] || !employeeId) return
          if (!['profitSharing', 'cashBalance'].includes(source)) return
          set((state) => withRecalculatedAnalysis(state, () => {
            const slot = deepClone(state.censusLocal.byPlanKey[planKey] ?? createEmptySlotLocal())
            if (amount === null || amount === undefined || amount === '') {
              delete slot.perPersonAmounts[source][employeeId]
            } else {
              const value = Number(amount)
              if (!Number.isFinite(value) || value < 0) return state
              slot.perPersonAmounts[source][employeeId] = value
            }
            return {
              censusLocal: {
                ...state.censusLocal,
                byPlanKey: { ...state.censusLocal.byPlanKey, [planKey]: slot },
              },
            }
          }, [planKey]))
        },
        setSolverTargets: (planKey, source, employeeIds) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey]) return
          if (!['profitSharing', 'cashBalance'].includes(source)) return
          set((state) => withRecalculatedAnalysis(state, () => {
            const slot = deepClone(state.censusLocal.byPlanKey[planKey] ?? createEmptySlotLocal())
            slot.solverTargets[source] = Array.isArray(employeeIds)
              ? [...new Set(employeeIds.filter((id) => typeof id === 'string' && id))]
              : []
            return {
              censusLocal: {
                ...state.censusLocal,
                byPlanKey: { ...state.censusLocal.byPlanKey, [planKey]: slot },
              },
            }
          }, [planKey]))
        },
        // Class membership is a census field, so setting it is an ordinary
        // census edit: one code, applying to every plan slot at once. There is
        // no per-plan sidecar and no rule engine to reconcile with.
        setEmployeeClass: (employeeId, label) => {
          if (!employeeId) return
          get().setEmployeeClasses([employeeId], label)
        },
        // Bulk form: one write, one recalculate, one undo step, so assigning a
        // whole class from the People table never re-runs the engine per row.
        // A blank label clears the code and drops those rows into the catch-all.
        setEmployeeClasses: (employeeIds, label) => {
          if (!(Array.isArray(employeeIds) && employeeIds.length)) return
          set((state) => withRecalculatedAnalysis(state, {
            employees: projectClassAssignment(state.employees, employeeIds, label),
          }))
        },
        // "Each participant in their own class" — the maximum-flexibility
        // new-comparability design.
        setIndividualEmployeeClasses: (employeeIds) => {
          if (!(Array.isArray(employeeIds) && employeeIds.length)) return
          set((state) => withRecalculatedAnalysis(state, {
            employees: projectIndividualClasses(state.employees, employeeIds),
          }))
        },

        // Applying a solve updates the authored plan, the census class codes,
        // AND the proposal provenance record in ONE transaction (§2.2), so one
        // undo restores every part. Membership is written to the census rows'
        // allocation_group, matching how a TPA proposal system tags a class.
        // `proposal` (optional) is the persisted
        // new-comparability proposal — goal, selected scenario, assumptions,
        // acknowledgment — stamped with the applied timestamp here so the
        // component and its provenance can never be written separately.
        // `cashBalance` (optional, combined DB/DC designs) carries label-keyed
        // credits ({label, method, value}) plus the interest-credit percent;
        // labels resolve to class ids against the SAME merged plan the
        // profit-sharing rates use.
        applyNewCompSolution: (planKey, design, groupAssignments, proposal = null, cashBalance = null) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey] || !design || !groupAssignments) return false
          set((state) => withRecalculatedAnalysis(state, () => {
            const currentPlan = state[planKey]
            const application = buildNewCompScenarioApplication(
              currentPlan, design, groupAssignments, cashBalance)
            const plan = mergePlan(currentPlan, {
              ...application.patch,
              ...(proposal
                ? { newCompProposal: { ...proposal, appliedAt: proposal.appliedAt || new Date().toISOString() } }
                : {}),
            })
            const employees = state.employees.map((employee) => {
              const label = groupAssignments[employee.id]
              return label === undefined
                ? employee
                : { ...employee, allocation_group: String(label ?? '').trim() }
            })
            return {
              [planKey]: plan,
              employees,
              journey: state.journey.planTouched ? state.journey : { ...state.journey, planTouched: true },
              inputErrors: {},
              inputValidationResetKey: state.inputValidationResetKey + 1,
            }
          }))
          return true
        },
        setSettingMetadata: (planKey, metadata) => {
          if (!PLAN_SLOTS.includes(planKey)) return
          set((state) => ({
            settingMetadataByPath: { ...state.settingMetadataByPath, [planKey]: metadata || {} },
          }))
        },
        // Duplicate Plan A (or an explicit source) into the lowest empty slot.
        // The new design joins the visible comparison immediately; no plan is
        // selected because all visible plans participate together.
        addPlan: (sourceKey = null) => {
          const state = get()
          const slot = PLAN_SLOTS.find((key) => !state[key])
          if (!slot) return null
          const fromKey = sourceKey && state[sourceKey] ? sourceKey : 'planA'
          set((current) => withRecalculatedAnalysis(current, {
            [slot]: copyPlanWithName(current[fromKey], PLAN_SLOT_NAMES[slot]),
            // The duplicate starts identical, including its participant-linked
            // local state (§2.2).
            censusLocal: {
              ...current.censusLocal,
              byPlanKey: {
                ...current.censusLocal.byPlanKey,
                [slot]: deepClone(current.censusLocal.byPlanKey[fromKey] ?? createEmptySlotLocal()),
              },
            },
            hiddenPlanKeys: current.hiddenPlanKeys.filter((key) => key !== slot),
            inputErrors: {},
            inputValidationResetKey: current.inputValidationResetKey + 1,
          }, [slot]))
          return slot
        },
        // Create a specific slot as a copy of Plan A when it doesn't exist yet.
        // Used by flows that target a named plan (assistant proposals, imports).
        ensurePlan: (planKey) => {
          if (!PLAN_SLOTS.includes(planKey)) return false
          if (get()[planKey]) return true
          set((current) => withRecalculatedAnalysis(current, {
            [planKey]: copyPlanWithName(current.planA, PLAN_SLOT_NAMES[planKey]),
            censusLocal: {
              ...current.censusLocal,
              byPlanKey: {
                ...current.censusLocal.byPlanKey,
                [planKey]: deepClone(current.censusLocal.byPlanKey.planA ?? createEmptySlotLocal()),
              },
            },
            hiddenPlanKeys: current.hiddenPlanKeys.filter((key) => key !== planKey),
            inputErrors: {},
            inputValidationResetKey: current.inputValidationResetKey + 1,
          }, [planKey]))
          return true
        },
        removePlan: (planKey) => {
          if (planKey === 'planA' || !PLAN_SLOTS.includes(planKey) || !get()[planKey]) return
          set((current) => withRecalculatedAnalysis(current, () => {
            const byPlanKey = { ...current.censusLocal.byPlanKey }
            delete byPlanKey[planKey]
            return {
              [planKey]: null,
              censusLocal: { ...current.censusLocal, byPlanKey },
              hiddenPlanKeys: current.hiddenPlanKeys.filter((key) => key !== planKey),
              activePlanKey: current.activePlanKey === planKey ? 'planA' : current.activePlanKey,
              inputErrors: {},
              inputValidationResetKey: current.inputValidationResetKey + 1,
            }
          }, [planKey]))
        },
        savePlanTemplate: (planKey, rawName) => {
          const state = get()
          if (!PLAN_SLOTS.includes(planKey) || !state[planKey]) return null
          const name = String(rawName || '').trim().replace(/\s+/g, ' ').slice(0, 40)
          if (!name) return null
          const existing = state.planTemplates.find(
            (template) => template.name.toLocaleLowerCase() === name.toLocaleLowerCase(),
          )
          const id = existing?.id || (
            globalThis.crypto?.randomUUID
              ? `saved-design-${globalThis.crypto.randomUUID()}`
              : `saved-design-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
          )
          const template = {
            id,
            name,
            plan: deepClone(state[planKey]),
            savedAt: new Date().toISOString(),
          }
          set({
            planTemplates: existing
              ? state.planTemplates.map((item) => item.id === id ? template : item)
              : [...state.planTemplates, template].slice(-20),
          })
          return { id, updated: Boolean(existing) }
        },
        applyPlanTemplate: (templateId, planKey = null) => {
          const state = get()
          const targetKey = planKey && state[planKey] ? planKey : 'planA'
          const template = state.planTemplates.find((item) => item.id === templateId)
          if (!template || !PLAN_SLOTS.includes(targetKey) || !state[targetKey]) return false
          const nextPlan = copyPlanWithName(template.plan, state[targetKey].name || PLAN_SLOT_NAMES[targetKey])
          const byPlanKey = {
            ...state.censusLocal.byPlanKey,
            [targetKey]: createEmptySlotLocal(),
          }
          set({
            [targetKey]: nextPlan,
            censusLocal: { ...state.censusLocal, byPlanKey },
            settingMetadataByPath: { ...state.settingMetadataByPath, [targetKey]: {} },
            analysis: {
              ...state.analysis,
              [targetKey]: calculateAnalysis(
                state.employees,
                nextPlan,
                targetKey,
                { ...state.censusLocal, byPlanKey },
              ),
            },
            lastCalculation: new Date().toISOString(),
            journey: state.journey.planTouched ? state.journey : { ...state.journey, planTouched: true },
            inputErrors: {},
            inputValidationResetKey: state.inputValidationResetKey + 1,
          })
          return true
        },
        removePlanTemplate: (templateId) => {
          if (!get().planTemplates.some((template) => template.id === templateId)) return false
          set((state) => ({
            planTemplates: state.planTemplates.filter((template) => template.id !== templateId),
          }))
          return true
        },
        setActivePlanKey: (planKey) => {
          if (!PLAN_SLOTS.includes(planKey) || !get()[planKey]) return
          const state = get()
          if (state.activePlanKey === planKey) return
          // Legacy/internal operation target only. Visibility is exclusively
          // managed by setPlanVisible so targeting a plan cannot make it join
          // or leave the workspace comparison as a side effect.
          set({ activePlanKey: planKey })
          // Plan-settings inputs share validation ids across plans, so a stale
          // error from the previous plan must not stick to the new one.
          get().resetInputValidation()
        },
        setPlanVisible: (planKey, visible) => {
          const state = get()
          if (!PLAN_SLOTS.includes(planKey) || !state[planKey]) return false
          if (planKey === 'planA') return visible !== false
          const hidden = state.hiddenPlanKeys.includes(planKey)
          if (visible && !hidden) return true
          if (!visible && hidden) return true
          if (!visible && visiblePlanKeys(state).length <= 1) return false

          const hiddenPlanKeys = visible
            ? state.hiddenPlanKeys.filter((key) => key !== planKey)
            : [...state.hiddenPlanKeys, planKey]
          const activePlanKey = !visible && state.activePlanKey === planKey
            ? visiblePlanKeys({ ...state, hiddenPlanKeys })[0] || 'planA'
            : state.activePlanKey
          set({ hiddenPlanKeys, activePlanKey })
          return true
        },
        resetPlanToBaseline: (planKey) => {
          const state = get()
          if (planKey === 'planA' || !state[planKey]) return
          set((current) => withRecalculatedAnalysis(current, {
            [planKey]: copyPlanWithName(current.planA, current[planKey].name || PLAN_SLOT_NAMES[planKey]),
            // Reset-to-baseline copies Plan A's sidecar entry too — clearing
            // it would not reproduce the baseline (§2.2).
            censusLocal: {
              ...current.censusLocal,
              byPlanKey: {
                ...current.censusLocal.byPlanKey,
                [planKey]: deepClone(current.censusLocal.byPlanKey.planA ?? createEmptySlotLocal()),
              },
            },
            inputErrors: {},
            inputValidationResetKey: current.inputValidationResetKey + 1,
          }, [planKey]))
        },

        addCustomChart: (chart) => {
          if (!chart?.config) return
          const id = chart.id || `chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
          // De-dupe an identical saved config so repeated "Add to my charts" on the
          // same chart does not stack copies.
          const signature = JSON.stringify({ planKey: chart.planKey, config: chart.config })
          const exists = get().customCharts.some(
            (saved) => JSON.stringify({ planKey: saved.planKey, config: saved.config }) === signature,
          )
          if (exists) return
          set((state) => ({ customCharts: [...state.customCharts, { ...chart, id, savedAt: Date.now() }] }))
        },
        removeCustomChart: (id) => {
          set((state) => ({ customCharts: state.customCharts.filter((chart) => chart.id !== id) }))
        },

        setReportConfig: (patch) => {
          set((state) => ({ reportConfig: { ...state.reportConfig, ...patch } }))
        },
        toggleReportSection: (key, enabled) => {
          set((state) => ({
            reportConfig: {
              ...state.reportConfig,
              sections: { ...state.reportConfig.sections, [key]: enabled },
            },
          }))
        },
        resetMatchTiers: (planKey = 'planA') => {
          get().updatePlan(planKey, {
            employerContributions: { matchTiers: deepClone(DEFAULT_MATCH_TIERS) },
          })
          get().resetInputValidation()
        },
        getPlanSettings: () => pickPlanSettings(get()),
        importPlanSettings: (settings) => {
          const normalized = normalizePlanSettings(settings, PLAN_SETTINGS_VERSION)
          // Estimate sessions round-trip through plan settings: loading one
          // enters estimate mode (stashing the real workspace) and regenerates
          // its synthetic census from the saved assumptions. Loading a normal
          // client while estimating exits the estimate first, so real settings
          // never silently apply to a synthetic census.
          if (normalized.estimate?.inputs) {
            get().enterEstimateMode(normalized.estimate.inputs)
          } else if (get().workspaceMode === 'estimate') {
            get().exitEstimateMode()
          }
          set((state) => withRecalculatedAnalysis(state, {
            planA: normalized.planA,
            planB: normalized.planB,
            planC: normalized.planC,
            planD: normalized.planD,
            // Importing replaces the plan-slot set, so every old sidecar entry
            // clears: solver targets and per-person amounts referenced plans
            // that no longer exist. Nothing is re-seeded, because membership is
            // not in the sidecar — the census keeps its own class codes, and a
            // plan file carries rates, never people. The census instance ID is
            // unchanged: the census itself did not change.
            censusLocal: {
              censusInstanceId: state.censusLocal.censusInstanceId,
              byPlanKey: {},
            },
            employerProfile: normalized.employerProfile,
            settingMetadataByPath: normalized.settingMetadataByPath,
            activePlanKey: 'planA',
            hiddenPlanKeys: [],
            journey: state.journey.planTouched ? state.journey : { ...state.journey, planTouched: true },
            inputErrors: {},
            inputValidationResetKey: state.inputValidationResetKey + 1,
          }))
        },

        openUtilityDialog: utilityDialog => set({ utilityDialog }),
        closeUtilityDialog: () => set({ utilityDialog: null }),
        setVisualizationTab: visualizationTab => set({ visualizationTab }),
        setDataExpanded: isDataExpanded => set({ isDataExpanded }),
        setCalculationsExpanded: isCalculationsExpanded => set({ isCalculationsExpanded }),

        recalculate: () => {
          const state = get()
          set({
            analysis: Object.fromEntries(PLAN_SLOTS.map((key) => [
              key,
              state[key] ? calculateAnalysis(state.employees, state[key], key, state.censusLocal) : emptyAnalysis(),
            ])),
            lastCalculation: new Date().toISOString(),
          })
        },

        getCalculatedEmployees: () => get().analysis.planA.calculatedEmployees,
        getPlanMetrics: () => get().analysis.planA.metrics,
        getChartData: () => get().analysis.planA.chartData,
        getTurnover: () => get().analysis.planA.turnover,
        getLimits: () => PLAN_LIMITS[get().planA.planYear],
      }),
      {
        partialize: getHistoryState,
        equality: historyStatesEqual,
        limit: 100,
      },
    ),
    {
      name: '401k-calculator-storage',
      version: 21,
      migrate: migratePersistedState,
      partialize: state => ({
        employees: state.employees,
        censusMode: state.censusMode,
        censusBaseline: state.censusBaseline,
        censusLocal: state.censusLocal,
        planA: state.planA,
        planB: state.planB,
        planC: state.planC,
        planD: state.planD,
        hiddenPlanKeys: state.hiddenPlanKeys,
        planTemplates: state.planTemplates,
        employerProfile: state.employerProfile,
        settingMetadataByPath: state.settingMetadataByPath,
        customCharts: state.customCharts,
        reportConfig: state.reportConfig,
        workspaceMode: state.workspaceMode,
        estimator: state.estimator,
        stashedActual: state.stashedActual,
        journey: state.journey,
        workspaceStage: state.workspaceStage,
      }),
      merge: (persistedState, currentState) => {
        const persisted = persistedState || {}
        // A persisted estimate session only counts when its assumptions came
        // with it – otherwise the workspace opens in actual mode.
        const inEstimate = persisted.workspaceMode === 'estimate' && persisted.estimator?.inputs
        const census = normalizePersistedCensus(
          persisted.employees ?? currentState.employees,
          persisted.censusMode ?? currentState.censusMode,
          persisted.censusBaseline,
        )
        const estimateIds = inEstimate
          ? migrateEstimateEmployeeIds(census.employees)
          : { employees: census.employees, idMap: {} }
        const plans = {
          planA: normalizePlan(persisted.planA || currentState.planA, 'Plan A'),
          planB: persisted.planB ? normalizePlan(persisted.planB, 'Plan B') : null,
          planC: persisted.planC ? normalizePlan(persisted.planC, 'Plan C') : null,
          planD: persisted.planD ? normalizePlan(persisted.planD, 'Plan D') : null,
        }
        const merged = {
          ...currentState,
          ...census,
          employees: estimateIds.employees,
          censusLocal: inEstimate
            ? remapCensusLocalEmployeeIds(
                persisted.censusLocal ?? currentState.censusLocal,
                estimateIds.idMap,
              )
            : normalizeCensusLocal(persisted.censusLocal ?? currentState.censusLocal),
          ...plans,
          activePlanKey: 'planA',
          hiddenPlanKeys: normalizeHiddenPlanKeys(persisted.hiddenPlanKeys, plans),
          planTemplates: normalizePlanTemplates(persisted.planTemplates),
          customCharts: Array.isArray(persisted.customCharts) ? persisted.customCharts : [],
          reportConfig: resolveReportConfig({
            ...currentState.reportConfig,
            ...(persisted.reportConfig || {}),
            sections: {
              ...currentState.reportConfig.sections,
              ...(persisted.reportConfig?.sections || {}),
            },
            proposal: {
              ...currentState.reportConfig.proposal,
              ...(persisted.reportConfig?.proposal || {}),
            },
          }),
          employerProfile: normalizeEmployerProfile(persisted.employerProfile),
          settingMetadataByPath: persisted.settingMetadataByPath || {},
          workspaceMode: inEstimate ? 'estimate' : 'actual',
          estimator: inEstimate ? persisted.estimator : null,
          stashedActual: inEstimate ? (persisted.stashedActual || null) : null,
          journey: normalizeJourney(persisted.journey),
          workspaceStage: WORKSPACE_STAGES.includes(persisted.workspaceStage)
            ? persisted.workspaceStage
            : 'people',
        }
        // Persisted authored inputs never hydrate beside the initializer's
        // empty analysis. The first observable hydrated state is coherent.
        return withRecalculatedAnalysis(currentState, merged)
      },
      onRehydrateStorage: () => state => {
        if (!state) return
        setTimeout(() => {
          useStore.temporal.getState().clear()
        }, 0)
      },
    },
  ),
)

export const undoStore = () => {
  const historyStore = useStore.temporal
  const history = historyStore.getState()
  if (history.pastStates.length === 0) return
  const current = getHistoryState(useStore.getState())
  const previous = history.pastStates[history.pastStates.length - 1]
  history.pause()
  try {
    useStore.setState((state) => withRecalculatedAnalysis(state, previous))
    historyStore.setState({
      pastStates: history.pastStates.slice(0, -1),
      futureStates: [...history.futureStates, current],
    })
  } finally {
    history.resume()
  }
}

export const redoStore = () => {
  const historyStore = useStore.temporal
  const history = historyStore.getState()
  if (history.futureStates.length === 0) return
  const current = getHistoryState(useStore.getState())
  const next = history.futureStates[history.futureStates.length - 1]
  history.pause()
  try {
    useStore.setState((state) => withRecalculatedAnalysis(state, next))
    historyStore.setState({
      pastStates: [...history.pastStates, current],
      futureStates: history.futureStates.slice(0, -1),
    })
  } finally {
    history.resume()
  }
}

export default useStore
